import React, { useRef } from 'react'
import { Upload, X, File, Eye, FileText } from 'lucide-react'
import { Button, InfoBox } from '../ui'

export type DocumentCategory = 'crlv' | 'invoice' | 'inspection' | 'transfer' | 'ipva' | 'licensing' | 'other'

export interface VehicleDocument {
 file: File
 category: DocumentCategory
}

interface VehicleDocumentUploadProps {
 label: string
 documents: VehicleDocument[]
 onDocumentsChange: (documents: VehicleDocument[]) => void
 accept?: string
 maxFiles?: number
 hint?: string
 className?: string
}

const documentCategoryLabels: Record<DocumentCategory, string> = {
 crlv: 'CRLV (Certificado de Registro)',
 invoice: 'Nota Fiscal',
 inspection: 'Laudo de Vistoria',
 transfer: 'Documento de Transferência',
 ipva: 'Comprovante IPVA',
 licensing: 'Licenciamento',
 other: 'Outros'
}

const documentCategoryColors: Record<DocumentCategory, string> = {
 crlv: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 invoice: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
 inspection: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
 transfer: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
 ipva: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
 licensing: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
 other: 'bg-surface text-foreground'
}

export const VehicleDocumentUpload: React.FC<VehicleDocumentUploadProps> = ({
 label,
 documents,
 onDocumentsChange,
 accept = '.pdf,.jpg,.jpeg,.png',
 maxFiles,
 hint,
 className = ''
}) => {
 const inputRef = useRef<HTMLInputElement>(null)

 const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 const selectedFiles = e.target.files
 if (selectedFiles && selectedFiles.length > 0) {
 const newDocuments = Array.from(selectedFiles).map(file => ({
 file,
 category: 'other' as DocumentCategory // Categoria padrão
 }))
 onDocumentsChange([...documents, ...newDocuments])
 }
 if (inputRef.current) {
 inputRef.current.value = ''
 }
 }

 const handleRemoveDocument = (index: number) => {
 onDocumentsChange(documents.filter((_, i) => i !== index))
 }

 const handleCategoryChange = (index: number, category: DocumentCategory) => {
 const updatedDocuments = [...documents]
 updatedDocuments[index] = { ...updatedDocuments[index], category }
 onDocumentsChange(updatedDocuments)
 }

 const handleViewFile = (file: File) => {
 const fileURL = URL.createObjectURL(file)
 window.open(fileURL, '_blank')
 setTimeout(() => {
 URL.revokeObjectURL(fileURL)
 }, 100)
 }

 const formatFileSize = (bytes: number): string => {
 if (bytes === 0) return '0 Bytes'
 const k = 1024
 const sizes = ['Bytes', 'KB', 'MB', 'GB']
 const i = Math.floor(Math.log(bytes) / Math.log(k))
 return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
 }

 const canAddMore = !maxFiles || documents.length < maxFiles

 return (
 <div className={className}>
 <label className="block text-sm font-medium text-foreground mb-2">
 {label}
 </label>

 {/* Upload Button */}
 <div className="mb-3">
 <input
 ref={inputRef}
 type="file"
 onChange={handleFileChange}
 accept={accept}
 multiple
 className="hidden"
 disabled={!canAddMore}
 />
 
 <Button
 type="button"
 variant="outline"
 onClick={() => inputRef.current?.click()}
 disabled={!canAddMore}
 className="w-full"
 >
 <Upload className="h-4 w-4 mr-2" />
 {documents.length === 0 ? 'Selecionar Documentos' : 'Adicionar Mais Documentos'}
 </Button>
 </div>

 {hint && (
 <InfoBox variant="info" className="mb-3">
 {hint}
 </InfoBox>
 )}

 {maxFiles && (
 <p className="text-xs text-muted-foreground mb-3">
 {documents.length}/{maxFiles} documento{documents.length !== 1 ? 's' : ''} {documents.length === 1 ? 'adicionado' : 'adicionados'}
 </p>
 )}

 {/* Lista de Documentos */}
 {documents.length > 0 && (
 <div className="space-y-3">
 {documents.map((doc, index) => (
 <div
 key={index}
 className="p-4 bg-card border border-border rounded-lg"
 >
 {/* Header do Documento */}
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-center space-x-3 flex-1 min-w-0">
 <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-foreground truncate">
 {doc.file.name}
 </p>
 <p className="text-xs text-muted-foreground">
 {formatFileSize(doc.file.size)}
 </p>
 </div>
 </div>
 <div className="flex items-center space-x-2 flex-shrink-0">
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => handleViewFile(doc.file)}
 className="p-1"
 title="Visualizar documento"
 >
 <Eye className="h-4 w-4" />
 </Button>
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => handleRemoveDocument(index)}
 className="p-1 text-red-600 hover:text-red-700 dark:text-red-400"
 title="Remover documento"
 >
 <X className="h-4 w-4" />
 </Button>
 </div>
 </div>

 {/* Seletor de Categoria */}
 <div className="space-y-2">
 <label className="block text-xs font-medium text-muted-foreground">
 Tipo de Documento *
 </label>
 <select
 value={doc.category}
 onChange={(e) => handleCategoryChange(index, e.target.value as DocumentCategory)}
 className="w-full px-3 py-2 text-sm border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-card text-foreground"
 >
 {Object.entries(documentCategoryLabels).map(([value, label]) => (
 <option key={value} value={value}>
 {label}
 </option>
 ))}
 </select>
 
 {/* Badge de Categoria */}
 <div className="mt-2">
 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${documentCategoryColors[doc.category]}`}>
 {documentCategoryLabels[doc.category]}
 </span>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}

 {/* Empty State */}
 {documents.length === 0 && (
 <div className="text-center py-8 bg-surface border-2 border-dashed border-border rounded-lg">
 <File className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
 <p className="text-sm text-muted-foreground mb-1">
 Nenhum documento adicionado
 </p>
 <p className="text-xs text-muted-foreground">
 Clique no botão acima para adicionar documentos
 </p>
 </div>
 )}
 </div>
 )
}

